import { uniq } from 'lodash-es';
import { paginationFactory } from 'redux/reusable/pagination';
import { xhrStatusFactory } from 'redux/reusable/xhrStatus';
import type { UserDraftProject, UserDraftProjectsTransform } from 'types/users';
import { reducerWithInitialState } from 'typescript-fsa-reducers';
import { uniqBy } from 'utils/typesafe';
import type { State } from './user.editor.types';
import * as types from './user.editor.types';
import { userEditorXhrModule, wrapPaginationReducer } from './user.editor.types';

const { withXhrStatusReducer } = userEditorXhrModule;

export const initialState: State = {
  draft: null,
  draftOriginal: null,
  projectsForDropdown: [],
  paginatedProjectRateList: [],
  projectRateFilters: {
    queryString: '',
    rateType: null,
  },
  xhrStatus: xhrStatusFactory(),
  pagination: paginationFactory(),
  projectsIdsByTagId: {},
};

const reducer = reducerWithInitialState<State>(initialState)
  .case(
    types.createDraft,
    (state, payload): State => ({
      ...state,
      draft: { ...payload, userAccountProperties: {} },
    })
  )
  .case(
    types.createDraftOriginal,
    (state, payload): State => ({
      ...state,
      draftOriginal: { ...payload, userAccountProperties: {} },
    })
  )
  .case(
    types.updateDraft,
    (state, payload): State => ({
      ...state,
      draft: state.draft == null ? null : { ...state.draft, ...payload },
    })
  )
  .case(types.updateDraftUserProperty, (state, payload): State => {
    return {
      ...state,
      draft:
        state.draft == null
          ? null
          : {
              ...state.draft,
              userAccountProperties: {
                ...state.draft?.userAccountProperties,
                [payload.id]: payload.value,
              },
            },
    };
  })
  .case(types.updateDraftDefaultHourRate, (state, payload): State => {
    if (state.draft == null) {
      return state;
    }

    const newState: State = {
      ...state,
      draft: {
        ...state.draft,
        default_hour_rate: payload,
      },
    };

    const isInvitingUser = state.draft.id == null;
    const updateInitial = isInvitingUser && projectPatchHasNoIndividualRates(state.draft.projects);
    if (updateInitial) {
      // For a new user, we update the "initial default hourly rate" so that it's whichever rate
      // the user has set before adding a individually-rated project.
      newState.draft!.initial_default_hour_rate = payload;
    }

    return newState;
  })
  .case(types.updateDraftInternalCostRate, (state, payload): State => {
    if (state.draft == null) {
      return state;
    }

    return {
      ...state,
      draft: {
        ...state.draft,
        internal_hour_rate: payload,
      },
    };
  })
  .case(types.clearDraft, () => initialState)
  .case(types.clearDraftOriginal, () => initialState)
  .case(types.removeTeamFromUser, (state, payload): State => {
    if (!state.draft || !state.draft.teams) {
      return state;
    }
    const teams = state.draft.teams.filter(t => t.id !== payload);
    const draft = { ...state.draft, teams };
    return {
      ...state,
      draft,
    };
  })

  .case(types.addProjectAssociationToDraft, (state, payload): State => {
    const draft = state.draft;
    if (!draft) {
      return state;
    }
    let projects = draft.projects;
    const projectId = payload.project_id;

    if (projects.spec === 'patch') {
      const isRemoved = projects.remove.includes(payload.project_id);
      projects = { ...projects, remove: projects.remove.filter(id => id !== projectId) };
      if (isRemoved) {
        // Add back
        projects.update = projects.update.concat(payload);
      } else {
        if (
          projects.add.find(({ project_id }) => project_id === payload.project_id) ||
          projects.update.find(({ project_id }) => project_id === payload.project_id)
        ) {
          // NOOP
          return state;
        }
        projects.add = projects.add.concat(payload);
      }
    } else {
      if (projects.projects.find(({ project_id }) => project_id === payload.project_id)) {
        // NOOP
        return state;
      }
      projects = {
        ...projects,
        projects: projects.projects.concat(payload),
      };
    }

    return {
      ...state,
      draft: {
        ...draft,
        projects,
      },
    };
  })

  .case(types.updateDraftProjectRate, (state, payload): State => {
    const draft = state.draft;
    if (!draft) {
      return state;
    }
    let projects = draft.projects;

    const updateInList = (
      list: Array<UserDraftProject>,
      append: boolean = false
    ): Array<UserDraftProject> => {
      if (list.find(({ project_id }) => project_id === payload.project_id)) {
        return list.map(association => {
          if (association.project_id === payload.project_id) {
            return payload;
          }
          return association;
        });
      } else if (append) {
        return list.concat(payload);
      }
      return list;
    };

    if (projects.spec === 'patch') {
      const isBeingAdded = projects.add.find(({ project_id }) => project_id === payload.project_id);
      const appendToUpdate = !isBeingAdded;
      projects = {
        ...projects,
        add: updateInList(projects.add),
        update: updateInList(projects.update, appendToUpdate),
      };
    } else {
      projects = {
        ...projects,
        projects: updateInList(projects.projects),
      };
    }

    return {
      ...state,
      draft: {
        ...draft,
        projects,
      },
    };
  })

  .case(types.removeProjectAssocationFromDraft, (state, payload): State => {
    const draft = state.draft;
    if (!draft) {
      return state;
    }
    let projects = draft.projects;

    if (projects.spec === 'patch') {
      const isBeingAdded = projects.add.find(({ project_id }) => project_id === payload);
      const toggleInRemovals = !isBeingAdded;

      projects = {
        ...projects,
        add: projects.add.filter(association => association.project_id !== payload),
        update: projects.update.filter(association => association.project_id !== payload),
        remove: toggleInRemovals ? uniq(projects.remove.concat(payload)) : projects.remove,
      };
    } else {
      projects = {
        ...projects,
        projects: projects.projects.filter(association => association.project_id !== payload),
      };
    }

    return {
      ...state,
      draft: {
        ...draft,
        projects,
      },
    };
  })

  .case(types.applyDefaultRateForChosenProjects, (state): State => {
    if (state.draft == null) {
      return state;
    }
    const { default_hour_rate: defaultRate } = state.draft;
    let { projects } = state.draft;

    const process = (list: Array<UserDraftProject>) =>
      list.map(udp => ({ ...udp, hour_rate: udp.hour_rate == null ? null : defaultRate }));

    if (projects.spec === 'patch') {
      projects = {
        ...projects,
        add: process(projects.add),
        update: process(projects.update),
      };
    } else {
      projects = {
        ...projects,
        projects: process(projects.projects),
      };
    }

    return {
      ...state,
      draft: {
        ...state.draft,
        projects,
        initial_default_hour_rate: defaultRate,
      },
    };
  })

  .case(types.disassociateAllProjects, (state): State => {
    const draft = state.draft;
    if (!draft) {
      return state;
    }

    return {
      ...state,
      draft: {
        ...draft,
        projects: { spec: 'replace', projects: [] },
      },
    };
  })

  .case(types.receiveProjectsForDropdown, (state, { projects, tagIds }): State => {
    const patch: Partial<State> = {
      projectsForDropdown: uniqBy(projects.concat(state.projectsForDropdown), 'id'),
    };

    if (tagIds?.length) {
      patch.projectsIdsByTagId = { ...state.projectsIdsByTagId };
      for (const tagId of tagIds) {
        patch.projectsIdsByTagId[tagId] = uniq(
          (patch.projectsIdsByTagId[tagId] ?? []).concat(projects.map(({ id }) => id))
        );
      }
    }

    return {
      ...state,
      ...patch,
    };
  })

  .case(types.receiveProjectsCountForClient, (state, payload): State => {
    const draft = state.draft;
    if (!draft) {
      return state;
    }
    return {
      ...state,
      draft: {
        ...draft,
        addedProjectsByClient: {
          ...draft.addedProjectsByClient,
          [payload.clientId]: payload.count,
        },
      },
    };
  })

  .case(
    types.receiveProjectsForRatesList,
    (state, payload): State => ({
      ...state,
      paginatedProjectRateList: payload,
    })
  )
  .case(
    types.setProjectRateFilters,
    (state, payload): State => ({
      ...state,
      projectRateFilters: {
        ...state.projectRateFilters,
        ...payload,
      },
    })
  );

export default wrapPaginationReducer(withXhrStatusReducer(reducer));

function projectPatchHasNoIndividualRates(projects: UserDraftProjectsTransform) {
  const withRate = (project: UserDraftProject) => project.hour_rate != null;
  if (projects.spec === 'patch') {
    return !projects.add.filter(withRate).length && !projects.update.filter(withRate).length;
  }
  return !projects.projects.filter(withRate).length;
}
